import React, { useState, useEffect, useRef } from 'react';
import { Tag, ChevronDown, PlusCircle, MinusCircle } from 'lucide-react';
import * as api from '../api';

/**
 * Filtre Include/Exclude sur les tags client (tiers_tags — Party Model).
 * Deux dropdowns : « Inclure » (le tiers doit avoir au moins un des tags)
 * et « Exclure » (le tiers ne doit en avoir aucun — ex. masquer les contentieux).
 * Un tag ne peut pas être à la fois inclus et exclu (exclusion mutuelle).
 *
 * Props :
 *  - includeIds: string[]        tag_ids actuellement inclus
 *  - excludeIds: string[]        tag_ids actuellement exclus
 *  - onChange: (inc, exc) => void
 *  - testidPrefix: string        préfixe des data-testid (défaut: 'tiers-tags')
 */
export default function TiersTagsFilter({ includeIds = [], excludeIds = [], onChange, testidPrefix = 'tiers-tags' }) {
  const [catalog, setCatalog] = useState([]);
  const [openMenu, setOpenMenu] = useState(null); // 'include' | 'exclude' | null
  const ref = useRef(null);

  useEffect(() => {
    api.getAnnuaireTags()
      .then(r => {
        const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        setCatalog(items.filter(t => t.pour_tiers && !['fonction', 'automation'].includes(t.tag_type)));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpenMenu(null); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const toggle = (tagId, mode) => {
    let inc = [...includeIds], exc = [...excludeIds];
    if (mode === 'include') {
      exc = exc.filter(id => id !== tagId); // exclusion mutuelle
      inc = inc.includes(tagId) ? inc.filter(id => id !== tagId) : [...inc, tagId];
    } else {
      inc = inc.filter(id => id !== tagId);
      exc = exc.includes(tagId) ? exc.filter(id => id !== tagId) : [...exc, tagId];
    }
    onChange(inc, exc);
  };

  const renderMenu = (mode) => {
    const ids = mode === 'include' ? includeIds : excludeIds;
    const accent = mode === 'include' ? '#15803D' : '#B91C1C';
    return (
      <div className="absolute z-50 mt-1 rounded-xl shadow-lg border bg-white py-1 w-64"
        style={{ borderColor: 'var(--border)', top: '100%', left: 0, maxHeight: '260px', overflowY: 'auto' }}
        data-testid={`${testidPrefix}-${mode}-menu`}>
        {catalog.length === 0 ? (
          <div className="px-3 py-2 text-xs" style={{ color: 'var(--text-muted)' }}>
            Aucun tag client — créez-en dans Paramètres
          </div>
        ) : catalog.map(t => {
          const checked = ids.includes(t.tag_id);
          return (
            <label key={t.tag_id}
              className="flex items-center gap-2.5 px-3 py-1.5 cursor-pointer hover:bg-gray-50 text-xs"
              style={{ color: 'var(--text)' }}>
              <input type="checkbox" className="w-3.5 h-3.5"
                style={{ accentColor: accent }}
                checked={checked}
                onChange={() => toggle(t.tag_id, mode)}
                data-testid={`${testidPrefix}-${mode}-cb-${t.tag_id}`} />
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: t.couleur }} />
              <span className="flex-1">{t.nom}</span>
            </label>
          );
        })}
      </div>
    );
  };

  const DropBtn = ({ mode, label, Icon, ids, accent, bg }) => (
    <div className="relative">
      <button type="button"
        data-testid={`${testidPrefix}-${mode}-btn`}
        onClick={() => setOpenMenu(v => v === mode ? null : mode)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all"
        style={{
          borderColor: ids.length > 0 ? accent : 'var(--border)',
          background: ids.length > 0 ? bg : 'white',
          color: ids.length > 0 ? accent : 'var(--text)',
        }}>
        <Icon size={12} />
        {label}
        {ids.length > 0 && (
          <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold"
            style={{ background: accent, color: '#fff' }}>
            {ids.length}
          </span>
        )}
        <ChevronDown size={10} style={{ transition: 'transform .15s', transform: openMenu === mode ? 'rotate(180deg)' : 'none' }} />
      </button>
      {openMenu === mode && renderMenu(mode)}
    </div>
  );

  return (
    <div className="flex items-center gap-2" ref={ref} data-testid={`${testidPrefix}-filter`}>
      <Tag size={13} style={{ color: 'var(--text-muted)' }} />
      <DropBtn mode="include" label="Inclure" Icon={PlusCircle} ids={includeIds} accent="#15803D" bg="#F0FDF4" />
      <DropBtn mode="exclude" label="Exclure" Icon={MinusCircle} ids={excludeIds} accent="#B91C1C" bg="#FEF2F2" />
    </div>
  );
}
